import React, { useState, useEffect, useRef } from 'react';
import { CustomerOrder, OrderOutlet, OrderItem } from '../../../types';
import {
  formatRupiah,
  createWhatsAppLink,
  formatOrderDate,
  formatOrderTime,
  formatOrderDateTime,
} from '../../../utils/formatters';
import { subscribeToSingleOrder } from '../../../utils/supabaseOrders';
import { subscribeCustomerOrder } from '../../../utils/oneSignal';
import { resolvePersistedOrderServiceFee } from '../../../utils/deliveryFee';
import {
  CheckCircle2,
  Clock,
  MessageCircle,
  Home,
  Copy,
  Check,
  Coffee,
  RefreshCw,
  Receipt,
  Store,
  MapPin,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  X,
  FileText,
  ChevronRight,
  Sparkles,
  Bell,
  Truck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface OrderConfirmationProps {
  order: CustomerOrder;
  outlet: OrderOutlet;
  onOrderAgain: () => void;
  onBackToHome: () => void;
}

/**
 * Resolves authoritative item unit price and subtotal for an order item.
 * Guarantees zero double-counting while ensuring add-ons (including customOptions, sizes,
 * toppings, and syrups) are accurately incorporated when item.price represents base price.
 */
export function resolveOrderItemPricing(
  item: OrderItem,
  currentOrder: CustomerOrder
): { unitPrice: number; itemSubtotal: number } {
  const qty = item.quantity || 1;

  // 1. Authoritative stored item subtotal if present
  const rawSubtotal =
    typeof (item as any).subtotal === 'number' && (item as any).subtotal > 0
      ? Number((item as any).subtotal)
      : null;

  // 2. Authoritative stored unitPrice or stored item price if present
  const rawUnitPrice =
    typeof item.unitPrice === 'number' && item.unitPrice > 0
      ? Number(item.unitPrice)
      : (typeof item.price === 'number' && item.price > 0
          ? Number(item.price)
          : null);

  if (rawSubtotal !== null) {
    return {
      unitPrice: rawUnitPrice ?? (rawSubtotal / qty),
      itemSubtotal: rawSubtotal,
    };
  }

  if (rawUnitPrice !== null) {
    return {
      unitPrice: rawUnitPrice,
      itemSubtotal: rawUnitPrice * qty,
    };
  }

  // 3. Fallback for un-persisted item with base price
  const basePrice = Number((item as any).basePrice ?? (item as any).product?.price ?? 0);
  const sizePrice = Number(item.size?.price) || 0;
  const topPrice = Number(item.topping?.price) || 0;
  const syrPrice = Number(item.syrup?.price) || 0;
  const customsPrice = (item.customOptions || []).reduce(
    (sum, c) => sum + (Number(c?.price) || 0),
    0
  );
  const addonsSum = sizePrice + topPrice + syrPrice + customsPrice;
  const unit = basePrice + addonsSum;

  return {
    unitPrice: unit,
    itemSubtotal: unit * qty,
  };
}

export const OrderConfirmation: React.FC<OrderConfirmationProps> = ({
  order: initialOrder,
  outlet,
  onOrderAgain,
  onBackToHome,
}) => {
  const [currentOrder, setCurrentOrder] = useState<CustomerOrder>(initialOrder);
  const [copied, setCopied] = useState(false);
  const [showReceiptDrawer, setShowReceiptDrawer] = useState(false);

  // Determine if the order belongs to a logged-in member or a guest
  const isGuest = Boolean(
    !currentOrder.customerId &&
    (() => {
      try {
        if (typeof localStorage !== 'undefined') {
          const raw = localStorage.getItem('leton_customer_profile');
          if (raw) {
            const parsed = JSON.parse(raw);
            return !parsed?.id;
          }
        }
        return true;
      } catch {
        return true;
      }
    })()
  );

  // Push Notification state for Guest Order Tracking (Legacy order_number tag fallback)
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [isSubscribingPush, setIsSubscribingPush] = useState(false);
  const [pushNotice, setPushNotice] = useState<string | null>(null);

  const handleEnablePushNotification = async () => {
    if (isSubscribingPush) return;
    setIsSubscribingPush(true);
    setPushNotice(null);
    try {
      const res = await subscribeCustomerOrder(currentOrder.orderNumber);
      if (res.success) {
        setPushSubscribed(true);
        setPushNotice('Notifikasi status aktif untuk pesanan ini.');
      } else {
        if (res.permission === 'denied' || res.error?.includes('ditolak')) {
          setPushNotice('Izin notifikasi tidak diizinkan di browser Anda.');
        } else {
          setPushNotice(res.error || 'Gagal mengaktifkan notifikasi.');
        }
      }
    } catch (err: any) {
      setPushNotice('Gagal mengaktifkan notifikasi.');
    } finally {
      setIsSubscribingPush(false);
    }
  };

  // Instantly scroll to top upon rendering confirmation page
  useEffect(() => {
    const scrollToTop = () => {
      try {
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, left: 0 });
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
        const scrollableElements = document.querySelectorAll(
          '.overflow-y-auto, .overflow-auto, [data-scroll-container="true"]'
        );
        scrollableElements.forEach((el) => {
          try {
            el.scrollTop = 0;
            if (typeof el.scrollTo === 'function') el.scrollTo({ top: 0, left: 0 });
          } catch (e) {}
        });
      } catch (e) {}
    };

    scrollToTop();
    const rafId = requestAnimationFrame(scrollToTop);
    const t1 = setTimeout(scrollToTop, 20);
    const t2 = setTimeout(scrollToTop, 100);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  // Live sync with database & in-app realtime status notification
  const lastNotifiedStatusRef = useRef<string>(initialOrder.orderStatus);
  const lastNotifiedPaymentStatusRef = useRef<string>(initialOrder.paymentStatus);
  const [inAppNotice, setInAppNotice] = useState<{ message: string; type: 'ready' | 'rejected' } | null>(null);

  useEffect(() => {
    setCurrentOrder(initialOrder);
  }, [initialOrder]);

  useEffect(() => {
    if (!initialOrder?.id) return;

    const unsubscribe = subscribeToSingleOrder(
      initialOrder.id,
      initialOrder.customerPhone,
      (updatedOrder) => {
        if (!updatedOrder) return;
        setCurrentOrder((prev) => ({
          ...prev,
          ...updatedOrder,
          deliveryFee: updatedOrder.deliveryFee ?? updatedOrder.delivery_fee ?? prev.deliveryFee ?? prev.delivery_fee,
          deliveryAddress: updatedOrder.deliveryAddress || updatedOrder.delivery_address || prev.deliveryAddress || prev.delivery_address,
          deliveryDistance: updatedOrder.deliveryDistance ?? updatedOrder.delivery_distance ?? prev.deliveryDistance ?? prev.delivery_distance,
          discountAmount: updatedOrder.discountAmount ?? (updatedOrder as any).discount_amount ?? prev.discountAmount ?? (prev as any).discount_amount,
          voucherCode: updatedOrder.voucherCode || (updatedOrder as any).voucher_code || prev.voucherCode || (prev as any).voucher_code,
          items: (updatedOrder.items && updatedOrder.items.length > 0) ? updatedOrder.items : prev.items,
        }));

        if (updatedOrder.paymentStatus !== lastNotifiedPaymentStatusRef.current) {
          const oldPayStatus = lastNotifiedPaymentStatusRef.current;
          lastNotifiedPaymentStatusRef.current = updatedOrder.paymentStatus;

          if (updatedOrder.paymentStatus === 'PAID' && oldPayStatus === 'WAITING PAYMENT') {
            setInAppNotice({
              message: 'Pembayaran DANA Terverifikasi! Pesanan Anda sedang disiapkan barista.',
              type: 'ready',
            });
          } else if (updatedOrder.paymentStatus === 'PAYMENT REJECTED' && oldPayStatus !== 'PAYMENT REJECTED') {
            setInAppNotice({
              message: `Pembayaran Ditolak / Gagal: ${updatedOrder.rejectionReason || 'Transaksi tidak berhasil'}`,
              type: 'rejected',
            });
          }
        }

        if (updatedOrder.orderStatus !== lastNotifiedStatusRef.current) {
          const oldStatus = lastNotifiedStatusRef.current;
          lastNotifiedStatusRef.current = updatedOrder.orderStatus;

          if (
            (updatedOrder.orderStatus === 'READY' || updatedOrder.orderStatus === 'COMPLETED') &&
            oldStatus !== 'READY' &&
            oldStatus !== 'COMPLETED'
          ) {
            setInAppNotice({
              message: '🎉 Pesanan Kamu Sudah Siap! Silakan ambil di outlet.',
              type: 'ready',
            });
          } else if (updatedOrder.orderStatus === 'CANCELLED' && oldStatus !== 'CANCELLED') {
            setInAppNotice({
              message: `Pesanan Ditolak: ${updatedOrder.rejectionReason || 'Tanpa alasan'}`,
              type: 'rejected',
            });
          }
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, [initialOrder?.id, initialOrder?.customerPhone]);

  const handleCopyOrderNumber = () => {
    navigator.clipboard.writeText(currentOrder.orderNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // Status mapping logic
  const isWaitingPayment = currentOrder.paymentStatus === 'WAITING PAYMENT';
  const isPaymentRejected = currentOrder.paymentStatus === 'PAYMENT REJECTED';
  const isDanaWaitingPayment =
    String(currentOrder.paymentMethod || '').toUpperCase() === 'DANA' && isWaitingPayment;
  const isOrderReady = !isWaitingPayment && !isPaymentRejected && currentOrder.orderStatus === 'READY';
  const isOrderCompleted = !isWaitingPayment && !isPaymentRejected && currentOrder.orderStatus === 'COMPLETED';
  const isOrderCancelled =
    (currentOrder.orderStatus as string) === 'CANCELLED' ||
    (currentOrder.orderStatus as string) === 'REJECTED' ||
    isPaymentRejected;
  const isPreparing =
    !isWaitingPayment &&
    !isOrderCancelled &&
    (currentOrder.orderStatus === 'PREPARING' ||
      currentOrder.orderStatus === 'NEW' ||
      currentOrder.orderStatus === 'ACCEPTED');

  // DANA Transaction Status Inquiry Recovery (controlled & non-aggressive)
  const [isCheckingDanaStatus, setIsCheckingDanaStatus] = useState(false);
  const [danaCheckNotice, setDanaCheckNotice] = useState<{
    message: string;
    type: 'info' | 'success' | 'error';
  } | null>(null);
  const isCheckingDanaRef = useRef(false);
  const lastDanaCheckAtRef = useRef<number>(0);
  const autoDanaCheckOrderIdRef = useRef<string | null>(null);

  const handleCheckDanaPaymentStatus = async (options?: { silent?: boolean }) => {
    const silent = Boolean(options?.silent);
    if (String(currentOrder.paymentMethod || '').toUpperCase() !== 'DANA') return;
    if (currentOrder.paymentStatus !== 'WAITING PAYMENT') return;
    if (isCheckingDanaRef.current) return;

    const now = Date.now();
    if (!silent && now - lastDanaCheckAtRef.current < 4000) {
      setDanaCheckNotice({
        message: 'Mohon tunggu beberapa detik sebelum memeriksa kembali.',
        type: 'info',
      });
      return;
    }

    isCheckingDanaRef.current = true;
    lastDanaCheckAtRef.current = now;
    setIsCheckingDanaStatus(true);
    if (!silent) {
      setDanaCheckNotice(null);
    }

    try {
      const targetOrderRef = currentOrder.orderNumber || currentOrder.id;
      const res = await fetch('/api/payment/dana/check-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: targetOrderRef }),
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok && data?.success) {
        const nextPayStatus = String(data.paymentStatus || currentOrder.paymentStatus);
        const nextOrderStatus = String(data.orderStatus || currentOrder.orderStatus);

        if (nextPayStatus === 'PAID') {
          lastNotifiedPaymentStatusRef.current = 'PAID';
          lastNotifiedStatusRef.current = nextOrderStatus as any;
          setCurrentOrder((prev) => ({
            ...prev,
            paymentStatus: 'PAID' as any,
            orderStatus: (nextOrderStatus || 'ACCEPTED') as any,
          }));
          setDanaCheckNotice(null);
          setInAppNotice({
            message: 'Pembayaran DANA Terverifikasi! Pesanan Anda sedang disiapkan barista.',
            type: 'ready',
          });
        } else if (nextPayStatus === 'PAYMENT REJECTED') {
          lastNotifiedPaymentStatusRef.current = 'PAYMENT REJECTED';
          lastNotifiedStatusRef.current = (nextOrderStatus || 'CANCELLED') as any;
          setCurrentOrder((prev) => ({
            ...prev,
            paymentStatus: 'PAYMENT REJECTED' as any,
            orderStatus: (nextOrderStatus || 'CANCELLED') as any,
          }));
          setDanaCheckNotice({
            message: data.message || 'Transaksi DANA dibatalkan atau kedaluwarsa.',
            type: 'error',
          });
        } else if (!silent) {
          setDanaCheckNotice({
            message:
              data.message ||
              'Pembayaran masih menunggu konfirmasi dari DANA. Jika sudah membayar, coba lagi beberapa saat.',
            type: 'info',
          });
        }
      } else if (!silent) {
        setDanaCheckNotice({
          message:
            data?.error ||
            'Belum dapat memeriksa status ke DANA saat ini. Silakan coba lagi beberapa saat.',
          type: 'error',
        });
      }
    } catch {
      if (!silent) {
        setDanaCheckNotice({
          message: 'Gagal terhubung ke server untuk memeriksa status pembayaran.',
          type: 'error',
        });
      }
    } finally {
      isCheckingDanaRef.current = false;
      setIsCheckingDanaStatus(false);
    }
  };

  // Controlled automatic recovery check when viewing a DANA order still in WAITING PAYMENT
  useEffect(() => {
    const orderKey = currentOrder.id || currentOrder.orderNumber;
    if (!orderKey || !isDanaWaitingPayment) return;

    const timers: Array<ReturnType<typeof setTimeout>> = [];

    if (autoDanaCheckOrderIdRef.current !== orderKey) {
      autoDanaCheckOrderIdRef.current = orderKey;
      timers.push(
        setTimeout(() => {
          handleCheckDanaPaymentStatus({ silent: true });
        }, 800)
      );
      timers.push(
        setTimeout(() => {
          handleCheckDanaPaymentStatus({ silent: true });
        }, 5500)
      );
      timers.push(
        setTimeout(() => {
          handleCheckDanaPaymentStatus({ silent: true });
        }, 12000)
      );
    }

    const onVisibilityChange = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible' &&
        Date.now() - lastDanaCheckAtRef.current >= 5000
      ) {
        handleCheckDanaPaymentStatus({ silent: true });
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      timers.forEach((t) => clearTimeout(t));
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
  }, [currentOrder.id, currentOrder.orderNumber, isDanaWaitingPayment]);

  // Delivery helper fields (Pickup & Dine-In guarantee Rp0 delivery fee)
  const isDelivery = currentOrder.orderType === 'DELIVERY';
  const deliveryAddress = isDelivery ? (currentOrder.deliveryAddress || currentOrder.delivery_address) : undefined;
  const deliveryFee = isDelivery ? Number(currentOrder.deliveryFee ?? currentOrder.delivery_fee ?? 0) : 0;
  const deliveryDistance = isDelivery ? (currentOrder.deliveryDistance ?? currentOrder.delivery_distance) : undefined;
  const discountAmount = Number(currentOrder.discountAmount ?? (currentOrder as any).discount_amount ?? 0);
  const voucherCode = currentOrder.voucherCode || (currentOrder as any).voucher_code;

  // Authoritative total reconciliation:
  // Computes items subtotal strictly following OrderDetailModal.tsx (Admin).
  // If the order is PICKUP (TAKE AWAY) or DINE IN, deliveryFee is guaranteed to be 0 (no delivery fee added).
  // If the stored totalAmount is deficient (e.g. server RPC omitted customOptions add-ons or delivery fee),
  // reconcile effectiveTotalAmount so Total Tagihan matches checkout intent.
  // Guarantees zero double counting of delivery fee and voucher discount.
  const computedItemsSubtotal = (currentOrder.items || []).reduce(
    (acc, it) => acc + resolveOrderItemPricing(it, currentOrder).itemSubtotal,
    0
  );

  const serviceFee = resolvePersistedOrderServiceFee({
    ...currentOrder,
    items: currentOrder.items,
    discountAmount,
    deliveryFee,
  });

  const effectiveTotalAmount = (() => {
    const baseTotal = Number(currentOrder.totalAmount || 0);
    if (baseTotal > 0) {
      return baseTotal;
    }
    return Math.max(0, computedItemsSubtotal - discountAmount) + deliveryFee + serviceFee;
  })();

  // Step Tracker State
  // Step 1: Diterima / Verifikasi Pembayaran
  const isStep1Done = !isWaitingPayment && !isOrderCancelled;
  // Step 2: Disiapkan
  const isStep2Active = isPreparing;
  const isStep2Done = isOrderReady || isOrderCompleted;
  // Step 3: Siap diambil / diantar
  const isStep3Active = isOrderReady;
  const isStep3Done = isOrderCompleted;

  // Total items count
  const totalItemsCount = (currentOrder.items || []).reduce((acc, item) => acc + (item.quantity || 1), 0);

  // WhatsApp Link Helper
  const waItemsText = (currentOrder.items || [])
    .map((it) => {
      const isDummyTopping =
        !it.topping ||
        !it.topping.name ||
        ['No Topping', 'Tanpa Topping', 'Tanpa Toppings', 'None'].includes(it.topping.name) ||
        (it.topping.id === 'top-no-topping' && (it.topping.price || 0) === 0);
      const isDummySyrup =
        !it.syrup ||
        !it.syrup.name ||
        ['No Syrup', 'Normal (Standard Recipe)', 'Normal', 'Tanpa Syrup', 'None'].includes(it.syrup.name) ||
        (it.syrup.id === 'syr-no-syrup' && (it.syrup.price || 0) === 0);

      const topPrice = Number(it.topping?.price) || 0;
      const syrPrice = Number(it.syrup?.price) || 0;
      const sizePrice = Number(it.size?.price) || 0;

      const topName = !isDummyTopping && it.topping?.name ? ` + Topping: ${it.topping.name}${topPrice > 0 ? ` (+${formatRupiah(topPrice)})` : ''}` : '';
      const syrName = !isDummySyrup && it.syrup?.name ? ` + Syrup: ${it.syrup.name}${syrPrice > 0 ? ` (+${formatRupiah(syrPrice)})` : ''}` : '';
      const sizeName = it.size?.name ? ` [Size: ${it.size.name}${sizePrice > 0 ? ` (+${formatRupiah(sizePrice)})` : ''}]` : '';
      const customsText = (it.customOptions || []).length > 0
        ? ' ' + (it.customOptions || []).map((c) => `[${c.groupName ? `${c.groupName}: ` : ''}${c.optionName}${Number(c.price) > 0 ? ` (+${formatRupiah(c.price)})` : ''}]`).join(' ')
        : '';

      const itQty = it.quantity || 1;
      const { itemSubtotal: itSubtotal } = resolveOrderItemPricing(it, currentOrder);

      return `• ${itQty}x ${it.name}${sizeName}${topName}${syrName}${customsText} (${formatRupiah(itSubtotal)})`;
    })
    .join('\n');

  const waDeliveryInfo = isDelivery
    ? `\n*Alamat Antar:* ${deliveryAddress || '-'}\n*Ongkos Kirim:* ${formatRupiah(deliveryFee)}`
    : '';

  const waDiscountInfo = discountAmount > 0
    ? `\n*Diskon Voucher:* -${formatRupiah(discountAmount)}${voucherCode ? ` (${voucherCode})` : ''}`
    : '';

  const waMessage = `*PESANAN ONLINE LETONKU* ☕
---------------------------------
*No. Order:* ${currentOrder.orderNumber}
*Tanggal:* ${formatOrderDate(currentOrder.createdAt)}
*Jam Order:* ${formatOrderTime(currentOrder.createdAt)}
*Outlet:* ${currentOrder.outletName}
*Customer:* ${currentOrder.customerName}
*Tipe:* ${currentOrder.orderType}${currentOrder.orderType === 'DINE IN' ? ` (Meja: ${currentOrder.tableNumber || '-'})` : ''}${waDeliveryInfo}
*Status:* ${currentOrder.orderStatus}

*Rincian Menu:*
${waItemsText}
*Subtotal Pesanan:* ${formatRupiah(computedItemsSubtotal)}${waDiscountInfo}
*Biaya Layanan:* ${formatRupiah(serviceFee)}

*TOTAL:* ${formatRupiah(effectiveTotalAmount)}
---------------------------------
Halo Barista ${currentOrder.outletName}, saya ingin menanyakan status pesanan nomor *${currentOrder.orderNumber}*. Terima kasih!`;

  const waLink = createWhatsAppLink(outlet.whatsapp || '6281234567890', waMessage);
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(currentOrder.outletName + ' ' + (outlet.address || 'Dumai Riau'))}`;

  const [viewMode, setViewMode] = useState<'confirmed' | 'tracking'>('confirmed');

  return (
    <div
      className="w-full bg-[#F8FAFC] min-h-screen text-[#0A192F] font-sans antialiased pb-36"
      style={{ paddingBottom: 'calc(9rem + env(safe-area-inset-bottom, 0px))' }}
    >
      {/* Realtime In-App Notice Toast */}
      <AnimatePresence>
        {inAppNotice && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3.5 rounded-2xl shadow-xl flex items-center gap-3 border backdrop-blur-md max-w-sm w-full mx-4 ${
              inAppNotice.type === 'ready'
                ? 'bg-[#0A192F] text-white border-blue-400/30'
                : 'bg-rose-950 text-white border-rose-500/50'
            }`}
            style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
          >
            {inAppNotice.type === 'ready' ? (
              <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-400 shrink-0" />
            )}
            <span className="font-semibold text-xs tracking-wide flex-1">
              {inAppNotice.message}
            </span>
            <button
              type="button"
              onClick={() => setInAppNotice(null)}
              className="p-1 text-white/70 hover:text-white rounded-full bg-white/10"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky Header when in Tracking View (UX Pilot Screen 9) */}
      {viewMode === 'tracking' && (
        <header 
          className="sticky top-0 z-30 bg-white/95 backdrop-blur-md px-4 pb-3 border-b border-[#E2E8F0] flex items-center justify-between"
          style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
        >
          <button
            type="button"
            onClick={() => setViewMode('confirmed')}
            className="w-10 h-10 rounded-full bg-slate-50 border border-[#E2E8F0] flex items-center justify-center text-[#0A192F] hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
          </button>
          <div className="text-center">
            <h1 className="text-base font-bold text-[#0A192F]">Order Status</h1>
            <p className="text-[11px] font-medium text-[#64748B]">#{currentOrder.orderNumber}</p>
          </div>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full bg-slate-50 border border-[#E2E8F0] flex items-center justify-center text-[#0A192F] hover:bg-slate-100 transition-colors"
            title="Contact Barista"
          >
            <MessageCircle className="w-4 h-4 text-[#0056D2]" />
          </a>
        </header>
      )}

      <main
        className="max-w-lg mx-auto px-4 pt-6 space-y-5"
        style={
          viewMode === 'confirmed'
            ? { paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }
            : undefined
        }
      >
        {viewMode === 'confirmed' ? (
          <>
            {/* Success / Status Header (UX Pilot Screen 8) */}
            <div className="text-center space-y-2 pt-2">
              <div
                className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-3 ring-8 ${
                  isOrderCancelled
                    ? 'bg-rose-50 text-rose-500 ring-rose-50/50'
                    : isWaitingPayment
                    ? 'bg-amber-50 text-amber-500 ring-amber-50/50'
                    : 'bg-emerald-50 text-emerald-500 ring-emerald-50/50'
                }`}
              >
                <div
                  className={`w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg ${
                    isOrderCancelled
                      ? 'bg-rose-500 shadow-rose-500/30'
                      : isWaitingPayment
                      ? 'bg-amber-500 shadow-amber-500/30'
                      : 'bg-emerald-500 shadow-emerald-500/30'
                  }`}
                >
                  {isOrderCancelled ? (
                    <X className="w-7 h-7" />
                  ) : isWaitingPayment ? (
                    <Clock className="w-7 h-7" />
                  ) : (
                    <Check className="w-7 h-7" />
                  )}
                </div>
              </div>
              <h1 className="text-2xl font-extrabold text-[#0A192F] tracking-tight">
                {isOrderCancelled
                  ? 'Order Cancelled'
                  : isWaitingPayment
                  ? 'Menunggu Konfirmasi Pembayaran'
                  : isOrderCompleted
                  ? (isDelivery ? 'Pesanan Selesai Diantar!' : 'Order Completed!')
                  : isOrderReady
                  ? (isDelivery ? 'Pesanan Siap Diantar!' : 'Order Ready for Pickup!')
                  : 'Order Confirmed!'}
              </h1>
              <p className="text-xs text-[#64748B] max-w-[280px] mx-auto leading-relaxed">
                {isOrderCancelled
                  ? currentOrder.rejectionReason || 'Sorry, your order could not be processed.'
                  : isWaitingPayment
                  ? `Menunggu konfirmasi pembayaran otomatis dari ${currentOrder.paymentMethod}. Pesanan akan diteruskan ke barista setelah pembayaran terverifikasi.`
                  : isDelivery
                  ? 'Terima kasih atas pesanan Anda! Barista sedang menyiapkan pesanan untuk pengantaran ke alamat Anda.'
                  : 'Thank you for your order! Your coffee is being prepared by our barista.'}
              </p>
            </div>

            {/* Pickup/Delivery Code & Status Card (UX Pilot Screen 8) */}
            <section className="bg-gradient-to-br from-[#0A192F] to-slate-800 rounded-2xl p-5 text-white shadow-lg relative overflow-hidden">
              <div className="absolute -right-6 -top-6 w-28 h-28 bg-[#0056D2]/20 rounded-full blur-xl" />
              <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-blue-400/10 rounded-full blur-lg" />

              <div className="relative z-10 flex items-center justify-between border-b border-white/10 pb-4 mb-4">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-blue-300 block">
                    {isDelivery ? 'Delivery Order Code' : 'Pickup Code'}
                  </span>
                  <span className="text-2xl sm:text-3xl font-extrabold tracking-wider mt-0.5 block text-blue-400 font-mono">
                    {currentOrder.orderNumber}
                  </span>
                </div>
                <div className="text-right">
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full border ${
                      isOrderCancelled
                        ? 'bg-rose-500/20 border-rose-400/30 text-rose-300'
                        : isWaitingPayment
                        ? 'bg-amber-500/20 border-amber-400/30 text-amber-300'
                        : 'bg-emerald-500/20 border-emerald-400/30 text-emerald-300'
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full animate-pulse ${
                        isOrderCancelled
                          ? 'bg-rose-400'
                          : isWaitingPayment
                          ? 'bg-amber-400'
                          : 'bg-emerald-400'
                      }`}
                    />
                    {isOrderCancelled
                      ? 'Cancelled'
                      : isWaitingPayment
                      ? 'Waiting Payment'
                      : isOrderCompleted
                      ? 'Completed'
                      : isOrderReady
                      ? (isDelivery ? 'Ready to Deliver' : 'Ready to Pick Up')
                      : (isDelivery ? 'Preparing Delivery' : 'Preparing')}
                  </span>
                </div>
              </div>

              <div className="relative z-10 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 text-slate-300">
                  {isWaitingPayment ? (
                    <Clock className="w-4 h-4 text-amber-400" />
                  ) : isDelivery ? (
                    <Truck className="w-4 h-4 text-blue-400" />
                  ) : (
                    <Clock className="w-4 h-4 text-blue-400" />
                  )}
                  <span>
                    {isWaitingPayment
                      ? 'Status Pembayaran'
                      : isDelivery
                      ? 'Estimasi Pengantaran'
                      : 'Estimated Pickup Time'}
                  </span>
                </div>
                <span className="font-bold text-white">
                  {isWaitingPayment
                    ? `Menunggu Verifikasi ${currentOrder.paymentMethod}`
                    : currentOrder.pickupTime || (isDelivery ? '15 - 30 Menit' : 'Ready in 10 - 15 Mins')}
                </span>
              </div>

              {isDanaWaitingPayment && (
                <div className="relative z-10 mt-4 pt-3.5 border-t border-white/10 space-y-2">
                  <button
                    type="button"
                    onClick={() => handleCheckDanaPaymentStatus({ silent: false })}
                    disabled={isCheckingDanaStatus}
                    className="w-full py-2.5 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0A192F] font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-sm"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isCheckingDanaStatus ? 'animate-spin' : ''}`} />
                    <span>
                      {isCheckingDanaStatus ? 'Memeriksa Status DANA...' : 'Periksa Status Pembayaran'}
                    </span>
                  </button>
                  {danaCheckNotice && (
                    <p
                      className={`text-[11px] text-center font-medium leading-relaxed ${
                        danaCheckNotice.type === 'error'
                          ? 'text-rose-300'
                          : danaCheckNotice.type === 'success'
                          ? 'text-emerald-300'
                          : 'text-amber-200'
                      }`}
                    >
                      {danaCheckNotice.message}
                    </p>
                  )}
                </div>
              )}
            </section>
          </>
        ) : (
          <>
            {/* UX Pilot Screen 9: Order Status Tracking Hero Card */}
            <section className="bg-white rounded-3xl p-6 shadow-xs border border-[#E2E8F0] text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100">
                <div
                  className={`h-full rounded-r-full transition-all duration-500 ${
                    isWaitingPayment ? 'bg-amber-500' : 'bg-[#0056D2]'
                  }`}
                  style={{
                    width: isOrderCompleted
                      ? '100%'
                      : isOrderReady
                      ? '85%'
                      : isWaitingPayment
                      ? '20%'
                      : '55%',
                  }}
                />
              </div>

              <div
                className={`w-20 h-20 mx-auto mt-2 mb-4 rounded-full flex items-center justify-center relative ${
                  isWaitingPayment ? 'bg-amber-50' : 'bg-blue-50'
                }`}
              >
                <div
                  className={`absolute inset-0 rounded-full border-2 border-dashed animate-spin opacity-30 ${
                    isWaitingPayment ? 'border-amber-500' : 'border-[#0056D2]'
                  }`}
                />
                {isWaitingPayment ? (
                  <Clock className="w-8 h-8 text-amber-600" />
                ) : (
                  <Coffee className="w-8 h-8 text-[#0056D2]" />
                )}
              </div>

              <span
                className={`inline-block px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase mb-2 ${
                  isWaitingPayment
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-blue-50 text-[#0056D2]'
                }`}
              >
                {isOrderCompleted
                  ? 'Order Completed'
                  : isOrderReady
                  ? 'Ready for Pickup'
                  : isWaitingPayment
                  ? 'Waiting for Payment'
                  : 'Preparing Your Order'}
              </span>
              <h2 className="text-xl font-extrabold text-[#0A192F] mb-1">
                {isOrderCompleted
                  ? 'Enjoy your coffee!'
                  : isOrderReady
                  ? 'Your coffee is ready at the counter!'
                  : isWaitingPayment
                  ? 'Menunggu Konfirmasi Pembayaran'
                  : 'Estimated Ready in 8 Mins'}
              </h2>
              <p className="text-xs text-[#64748B]">
                {isWaitingPayment
                  ? `Menunggu konfirmasi otomatis dari ${currentOrder.paymentMethod} sebelum pesanan disiapkan`
                  : 'Our barista is crafting your coffee with care'}
              </p>

              {/* Pickup Code Box */}
              <div className="mt-5 bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-3.5 flex items-center justify-between">
                <div className="text-left">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B] block">
                    Pickup Code
                  </span>
                  <span className="text-xl font-extrabold text-[#0A192F] tracking-tight font-mono">
                    {currentOrder.orderNumber}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleCopyOrderNumber}
                  className="px-3 py-2 rounded-xl bg-white border border-[#E2E8F0] shadow-2xs text-xs font-bold text-[#0056D2] flex items-center gap-1.5 cursor-pointer"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  <span>{copied ? 'Copied' : 'Copy Code'}</span>
                </button>
              </div>

              {isDanaWaitingPayment && (
                <div className="mt-3 space-y-2">
                  <button
                    type="button"
                    onClick={() => handleCheckDanaPaymentStatus({ silent: false })}
                    disabled={isCheckingDanaStatus}
                    className="w-full py-2.5 px-4 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0A192F] font-bold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isCheckingDanaStatus ? 'animate-spin' : ''}`} />
                    <span>
                      {isCheckingDanaStatus ? 'Memeriksa Status DANA...' : 'Periksa Status Pembayaran'}
                    </span>
                  </button>
                  {danaCheckNotice && (
                    <p
                      className={`text-[11px] font-medium leading-relaxed ${
                        danaCheckNotice.type === 'error'
                          ? 'text-rose-600'
                          : danaCheckNotice.type === 'success'
                          ? 'text-emerald-600'
                          : 'text-amber-700'
                      }`}
                    >
                      {danaCheckNotice.message}
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* UX Pilot Screen 9: Order Timeline */}
            <section className="bg-white rounded-3xl p-5 shadow-xs border border-[#E2E8F0]">
              <h3 className="text-sm font-bold text-[#0A192F] mb-4">Order Timeline</h3>
              <div className="relative pl-6 space-y-6 before:content-[''] before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                {/* Step 1: Payment / Order Confirmation */}
                <div className="relative flex items-start justify-between">
                  <div
                    className={`absolute -left-6 top-0.5 w-6 h-6 rounded-full text-white flex items-center justify-center ring-4 ring-white shadow-xs ${
                      isWaitingPayment
                        ? 'bg-amber-500 animate-pulse'
                        : isOrderCancelled
                        ? 'bg-rose-500'
                        : 'bg-[#10B981]'
                    }`}
                  >
                    {isWaitingPayment ? (
                      <Clock className="w-3 h-3" />
                    ) : isOrderCancelled ? (
                      <X className="w-3 h-3" />
                    ) : (
                      <Check className="w-3 h-3" />
                    )}
                  </div>
                  <div className="pl-2">
                    <h4 className="text-xs font-bold text-[#0A192F]">
                      {isWaitingPayment
                        ? 'Waiting for Payment'
                        : isOrderCancelled
                        ? 'Order Cancelled'
                        : 'Order Confirmed'}
                    </h4>
                    <p className="text-[11px] text-[#64748B] mt-0.5">
                      {isWaitingPayment
                        ? `Menunggu konfirmasi pembayaran via ${currentOrder.paymentMethod}`
                        : currentOrder.paymentStatus === 'WAITING VERIFICATION'
                        ? `Menunggu verifikasi bukti pembayaran ${currentOrder.paymentMethod}`
                        : currentOrder.paymentStatus === 'PAY AT STORE'
                        ? 'Pembayaran tunai di kasir'
                        : isPaymentRejected
                        ? `Pembayaran ${currentOrder.paymentMethod} ditolak / gagal`
                        : `Payment verified via ${currentOrder.paymentMethod}`}
                    </p>
                  </div>
                  <span className="text-[11px] font-semibold text-[#64748B]">
                    {formatOrderTime(currentOrder.createdAt)}
                  </span>
                </div>

                {/* Step 2: Preparing Order */}
                <div
                  className={`relative flex items-start justify-between ${
                    isStep2Active || isStep2Done ? 'opacity-100' : 'opacity-45'
                  }`}
                >
                  <div
                    className={`absolute -left-6 top-0.5 w-6 h-6 rounded-full flex items-center justify-center ring-4 shadow-sm ${
                      isStep2Done
                        ? 'bg-[#10B981] text-white ring-white'
                        : isStep2Active
                        ? 'bg-[#0056D2] text-white ring-blue-50 animate-pulse'
                        : 'bg-slate-200 text-slate-500 ring-white'
                    }`}
                  >
                    {isStep2Done ? <Check className="w-3 h-3" /> : <Coffee className="w-3 h-3" />}
                  </div>
                  <div className="pl-2">
                    <h4
                      className={`text-xs font-bold ${
                        isStep2Active ? 'text-[#0056D2]' : 'text-[#0A192F]'
                      }`}
                    >
                      Preparing Order
                    </h4>
                    <p className="text-[11px] text-[#64748B] mt-0.5">
                      {isWaitingPayment
                        ? 'Menunggu pembayaran terkonfirmasi sebelum disiapkan barista'
                        : `Barista at ${currentOrder.outletName} is making your drinks`}
                    </p>
                  </div>
                  <span
                    className={`text-[11px] ${
                      isStep2Active
                        ? 'font-bold text-[#0056D2]'
                        : 'font-medium text-[#64748B]'
                    }`}
                  >
                    {isStep2Done ? 'Done' : isStep2Active ? 'In Progress' : 'Pending'}
                  </span>
                </div>

                {/* Step 3: Ready for Pickup / Delivery */}
                <div
                  className={`relative flex items-start justify-between ${
                    isStep3Active || isStep3Done ? 'opacity-100' : 'opacity-45'
                  }`}
                >
                  <div
                    className={`absolute -left-6 top-0.5 w-6 h-6 rounded-full flex items-center justify-center ring-4 ring-white ${
                      isStep3Done
                        ? 'bg-[#10B981] text-white'
                        : isStep3Active
                        ? 'bg-[#0056D2] text-white animate-bounce'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {isDelivery ? <Truck className="w-3 h-3" /> : <Store className="w-3 h-3" />}
                  </div>
                  <div className="pl-2">
                    <h4 className="text-xs font-bold text-[#0A192F]">
                      {isDelivery ? 'Siap Diantar' : 'Ready for Pickup'}
                    </h4>
                    <p className="text-[11px] text-[#64748B] mt-0.5">
                      {isDelivery
                        ? `Pesanan siap dikirim dari ${currentOrder.outletName} ke alamat Anda`
                        : `Pick up at counter with code #${currentOrder.orderNumber}`}
                    </p>
                  </div>
                  <span className="text-[11px] font-medium text-[#64748B]">
                    {isStep3Active || isStep3Done ? (isDelivery ? 'Siap Antar' : 'Ready!') : 'Soon'}
                  </span>
                </div>
              </div>
            </section>
          </>
        )}

        {/* Guest Push Notification Prompt */}
        {isGuest && !isOrderCompleted && !isOrderCancelled && (
          <div className="p-3.5 rounded-2xl bg-blue-50/70 border border-blue-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                  pushSubscribed ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-[#0056D2] shadow-2xs'
                }`}
              >
                {pushSubscribed ? <Check className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-[#0A192F] truncate">
                  {pushSubscribed ? 'Live Notification Active' : 'Enable Order Notifications'}
                </p>
                <p className="text-[11px] text-[#64748B] leading-tight truncate">
                  {pushNotice ||
                    (pushSubscribed
                      ? 'We will alert you when your coffee is ready.'
                      : 'Get notified automatically when your order is ready.')}
                </p>
              </div>
            </div>
            {!pushSubscribed && (
              <button
                type="button"
                onClick={handleEnablePushNotification}
                disabled={isSubscribingPush}
                className="px-3 py-1.5 rounded-xl bg-[#0056D2] hover:bg-blue-700 text-white text-xs font-bold shrink-0 transition-all cursor-pointer shadow-2xs disabled:opacity-50"
              >
                {isSubscribingPush ? 'Wait...' : 'Enable'}
              </button>
            )}
          </div>
        )}

        {/* Delivery Destination Section (when Delivery) */}
        {isDelivery && (
          <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#0056D2] bg-blue-50 px-2 py-0.5 rounded-md">
                Alamat Pengantaran
              </span>
              {deliveryDistance !== null && deliveryDistance !== undefined && (
                <span className="text-xs font-bold text-[#64748B]">
                  Jarak: {Number(deliveryDistance).toFixed(1)} km
                </span>
              )}
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0056D2] flex items-center justify-center shrink-0">
                <Truck className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-sm text-[#0A192F]">{currentOrder.customerName}</h3>
                <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">
                  {deliveryAddress || 'Alamat tidak tersedia'}
                </p>
                <p className="text-[11px] text-[#0056D2] font-semibold mt-1">
                  Dikirim dari {currentOrder.outletName}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Outlet Information (when Self Pick-Up) */}
        {!isDelivery && (
          <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
                Pick-Up Outlet
              </span>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-bold text-[#0056D2] flex items-center gap-1 hover:underline"
              >
                <span>Directions</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0056D2] flex items-center justify-center shrink-0">
                <Store className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm text-[#0A192F]">{currentOrder.outletName}</h3>
                <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">
                  {outlet.address || 'Dumai, Riau'}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Order Summary / Details Card (UX Pilot Screen 8) */}
        <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-3.5">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <span className="text-[10px] text-[#64748B] font-medium block">Order ID</span>
              <span className="text-xs font-bold text-[#0A192F] font-mono">
                #{currentOrder.orderNumber}
              </span>
            </div>
            <button
              type="button"
              onClick={handleCopyOrderNumber}
              className="text-xs font-semibold text-[#64748B] hover:text-[#0A192F] flex items-center gap-1 bg-slate-50 px-2.5 py-1 rounded-md border border-[#E2E8F0] cursor-pointer"
            >
              {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>

          {/* Items List */}
          <div className="space-y-3">
            {(currentOrder.items || []).map((item, idx) => {
              const qty = item.quantity || 1;
              const { unitPrice, itemSubtotal } = resolveOrderItemPricing(item, currentOrder);

              const isDummyTopping =
                !item.topping ||
                !item.topping.name ||
                ['No Topping', 'Tanpa Topping', 'Tanpa Toppings', 'None'].includes(item.topping.name) ||
                (item.topping.id === 'top-no-topping' && (item.topping.price || 0) === 0);
              const isDummySyrup =
                !item.syrup ||
                !item.syrup.name ||
                ['No Syrup', 'Normal (Standard Recipe)', 'Normal', 'Tanpa Syrup', 'None'].includes(item.syrup.name) ||
                (item.syrup.id === 'syr-no-syrup' && (item.syrup.price || 0) === 0);

              const topPrice = Number(item.topping?.price) || 0;
              const syrPrice = Number(item.syrup?.price) || 0;
              const sizePrice = Number(item.size?.price) || 0;

              const customOptionLabels = (item.customOptions || []).map(
                (co) => `${co.groupName ? `${co.groupName}: ` : ''}${co.optionName}${Number(co.price) > 0 ? ` (+${formatRupiah(co.price)})` : ''}`
              );

              const detailsList = [
                item.size?.name ? `Size: ${item.size.name}${sizePrice > 0 ? ` (+${formatRupiah(sizePrice)})` : ''}` : null,
                !isDummyTopping && item.topping?.name ? `Topping: ${item.topping.name}${topPrice > 0 ? ` (+${formatRupiah(topPrice)})` : ''}` : null,
                !isDummySyrup && item.syrup?.name ? `Syrup: ${item.syrup.name}${syrPrice > 0 ? ` (+${formatRupiah(syrPrice)})` : ''}` : null,
                ...customOptionLabels,
              ].filter(Boolean);

              const details = detailsList.join(' • ');

              return (
                <div key={idx} className="flex justify-between items-start text-xs">
                  <div className="flex gap-2.5 min-w-0 pr-2">
                    <span className="font-bold text-[#0056D2] bg-blue-50 w-5 h-5 rounded flex items-center justify-center shrink-0">
                      {qty}x
                    </span>
                    <div className="min-w-0">
                      <h4 className="font-bold text-[#0A192F] truncate">{item.name}</h4>
                      {details && <p className="text-[#64748B] mt-0.5 truncate">{details}</p>}
                      {item.note && (
                        <p className="text-[11px] text-[#0056D2] italic mt-0.5">"{item.note}"</p>
                      )}
                      {qty > 1 && (
                        <span className="text-[10px] text-[#64748B] block mt-0.5">
                          @ {formatRupiah(unitPrice)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="font-bold text-[#0A192F] shrink-0">
                    {formatRupiah(itemSubtotal)}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Payment Summary */}
          <div className="border-t border-dashed border-[#E2E8F0] pt-3 space-y-2">
            <div className="flex justify-between text-xs text-[#64748B]">
              <span>Payment Method</span>
              <span className="font-bold text-[#0A192F] flex items-center gap-1">
                {currentOrder.paymentMethod}
              </span>
            </div>
            <div className="flex justify-between text-xs text-[#64748B]">
              <span>Payment Status</span>
              <span
                className={`font-bold ${
                  currentOrder.paymentStatus === 'PAID'
                    ? 'text-emerald-600'
                    : isWaitingPayment
                    ? 'text-amber-600'
                    : isPaymentRejected
                    ? 'text-rose-600'
                    : 'text-[#0A192F]'
                }`}
              >
                {currentOrder.paymentStatus}
              </span>
            </div>
            <div className="flex justify-between text-xs text-[#64748B]">
              <span>Subtotal Pesanan</span>
              <span className="font-semibold text-[#0A192F]">
                {formatRupiah(computedItemsSubtotal)}
              </span>
            </div>
            {discountAmount > 0 && (
              <div className="flex justify-between text-xs text-emerald-600">
                <span className="flex items-center gap-1 font-medium">
                  <span>Diskon Voucher {voucherCode ? `(${voucherCode})` : ''}</span>
                </span>
                <span className="font-semibold text-emerald-600">
                  - {formatRupiah(discountAmount)}
                </span>
              </div>
            )}
            {isDelivery && deliveryFee > 0 && (
              <div className="flex justify-between text-xs text-[#0A192F]">
                <span className="flex items-center gap-1">
                  <Truck className="w-3.5 h-3.5 text-[#0056D2]" />
                  <span>Ongkos Kirim {deliveryDistance ? `(${Number(deliveryDistance).toFixed(1)} km)` : ''}</span>
                </span>
                <span className="font-semibold text-[#0056D2]">
                  {formatRupiah(deliveryFee)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-xs text-[#64748B]">
              <span>Biaya Layanan</span>
              <span className="font-semibold text-[#0A192F]">
                {formatRupiah(serviceFee)}
              </span>
            </div>
            <div className="flex justify-between text-xs text-[#64748B] pt-1 border-t border-slate-100">
              <span className="font-bold text-[#0A192F]">{currentOrder.paymentStatus === 'PAID' ? 'Total Pembayaran (Lunas)' : 'Total Pembayaran'}</span>
              <span className="font-extrabold text-sm text-[#0056D2]">
                {formatRupiah(effectiveTotalAmount)}
              </span>
            </div>
          </div>

          {/* Points Earned Banner */}
          <div className="bg-amber-50 border border-amber-200/60 rounded-xl p-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-amber-400 text-[#0A192F] flex items-center justify-center text-xs font-bold">
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <span className="text-xs font-semibold text-amber-900">Loyalty Points Earned</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowReceiptDrawer(true)}
                className="text-[11px] font-bold text-[#0056D2] hover:underline cursor-pointer"
              >
                Digital Receipt
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Fixed Bottom Action Buttons (UX Pilot Screen 8) */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] p-4 shadow-[0_-8px_20px_rgba(10,25,47,0.06)] z-40"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-lg mx-auto space-y-2.5">
          {viewMode === 'confirmed' ? (
            <button
              type="button"
              onClick={() => setViewMode('tracking')}
              className="w-full bg-[#0056D2] hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl shadow-md shadow-blue-600/20 transition-all flex items-center justify-center gap-2 text-sm cursor-pointer"
            >
              <Clock className="w-4 h-4" />
              <span>Track Order Status</span>
            </button>
          ) : (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-[#0056D2] hover:bg-blue-700 text-white font-bold py-3.5 rounded-xl shadow-md shadow-blue-600/20 transition-all flex items-center justify-center gap-2 text-sm"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Chat Barista on WhatsApp</span>
            </a>
          )}
          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={onOrderAgain}
              className="w-full bg-slate-50 hover:bg-slate-100 text-[#0A192F] font-bold py-3 rounded-xl border border-[#E2E8F0] transition-all flex items-center justify-center gap-1.5 text-xs cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5 text-[#0056D2]" />
              <span>Order More</span>
            </button>
            <button
              type="button"
              onClick={onBackToHome}
              className="w-full bg-white hover:bg-slate-50 text-[#0A192F] font-bold py-3 rounded-xl border border-[#E2E8F0] transition-all flex items-center justify-center gap-1.5 text-xs cursor-pointer"
            >
              <Home className="w-3.5 h-3.5 text-[#64748B]" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>
      </div>

      {/* Digital Receipt Modal */}
      <AnimatePresence>
        {showReceiptDrawer && (
          <div className="fixed inset-0 z-50 bg-[#0A192F]/50 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-lg bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-xl flex flex-col gap-4 max-h-[85vh] overflow-y-auto"
              style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
            >
              <div className="flex items-center justify-between pb-3 border-b border-[#E2E8F0]">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-bold block">
                    Digital Receipt
                  </span>
                  <span className="text-base font-extrabold text-[#0A192F]">
                    LetonKu • {currentOrder.outletName}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowReceiptDrawer(false)}
                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[#64748B] hover:text-[#0A192F] cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="bg-[#F8FAFC] p-4 rounded-2xl border border-[#E2E8F0] flex flex-col gap-2.5 text-xs text-[#64748B]">
                <div className="flex justify-between items-center">
                  <span>Order ID</span>
                  <span className="font-bold text-[#0A192F] font-mono">#{currentOrder.orderNumber}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Date & Time</span>
                  <span className="font-semibold text-[#0A192F]">
                    {formatOrderDateTime(currentOrder.createdAt)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Customer</span>
                  <span className="font-semibold text-[#0A192F]">{currentOrder.customerName}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Tipe Pesanan</span>
                  <span className="font-semibold text-[#0A192F]">
                    {isDelivery ? 'Delivery (Pesan Antar)' : currentOrder.orderType}
                  </span>
                </div>
                {isDelivery && deliveryAddress && (
                  <div className="flex justify-between items-start gap-2">
                    <span className="shrink-0">Alamat Antar</span>
                    <span className="font-semibold text-[#0A192F] text-right max-w-[200px] truncate">
                      {deliveryAddress}
                    </span>
                  </div>
                )}
                {isDelivery && deliveryFee > 0 && (
                  <div className="flex justify-between items-center">
                    <span>Ongkos Kirim</span>
                    <span className="font-semibold text-[#0A192F]">
                      {formatRupiah(deliveryFee)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span>Subtotal Pesanan</span>
                  <span className="font-semibold text-[#0A192F]">
                    {formatRupiah(computedItemsSubtotal)}
                  </span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between items-center text-emerald-600">
                    <span>Diskon Voucher</span>
                    <span className="font-semibold">-{formatRupiah(discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span>Biaya Layanan</span>
                  <span className="font-semibold text-[#0A192F]">
                    {formatRupiah(serviceFee)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Payment</span>
                  <span className="font-semibold text-[#0A192F] uppercase">
                    {currentOrder.paymentMethod} ({currentOrder.paymentStatus})
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-[#E2E8F0]">
                  <span className="font-bold text-[#0A192F]">Total Pembayaran</span>
                  <span className="font-extrabold text-sm text-[#0056D2]">
                    {formatRupiah(effectiveTotalAmount)}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowReceiptDrawer(false)}
                className="w-full py-3 bg-[#0A192F] text-white rounded-xl text-xs font-bold mt-1 cursor-pointer"
              >
                Close Receipt
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
